import { Tooltip, useMediaQuery, useTheme } from '@mui/material';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { useState } from 'react';
import { Link } from 'react-router-dom';

import { COLORS } from './constants';

import {
  ChecklistItem,
  getHOADisclosureTier,
  HOAAnnualBudgetFindings,
  HOABoardMeetingEntry,
  HOABoardMinutesFindings,
  HOAEnvironmentalFindings,
  HOAFindings,
  HOADisclosureTier,
} from '@/types/HOA';

// ─── Hero findings — up to one per domain, not one worst-overall pick ─────
// A buyer thinks in terms of legal/environmental risk, financial risk, and
// governance risk as separate questions, not a single flattened "worst
// thing" ranking (which would also make an environmental covenant and a
// late fee directly comparable on one scale — they aren't). Each domain
// independently stays silent when nothing's flagged (mirrors NHD's
// pickHeroFinding "quiet when clean" convention), so a healthy property
// shows 0-1 callouts, never padded to a fixed count. Shared between the
// summary card (compact) and HOADetailView (fuller TLDR) so the two never
// disagree about what's flagged.

export type HOAHeroTone = 'critical' | 'urgent';
export type HOAHeroVariant =
  | 'environmental-covenant'
  | 'litigation'
  | 'delinquency'
  | 'violation'
  | 'assessment'
  | 'reserve-underfunded'
  | 'governance-cadence';

export interface HOAHeroFinding {
  variant: HOAHeroVariant;
  tone: HOAHeroTone;
  headline: string;
  detail: string;
}

const LitigationIcon = (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="12" y1="3" x2="12" y2="21" />
    <line x1="5" y1="7" x2="19" y2="7" />
    <path d="M4 7l-2 5a3 3 0 0 0 6 0z" />
    <path d="M20 7l-2 5a3 3 0 0 0 6 0z" />
  </svg>
);

const DelinquencyIcon = (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="2" y="6" width="20" height="12" rx="2" />
    <circle cx="12" cy="12" r="3" />
    <line x1="6" y1="12" x2="6.01" y2="12" />
    <line x1="18" y1="12" x2="18.01" y2="12" />
  </svg>
);

const ViolationIcon = (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <line x1="5" y1="3" x2="5" y2="21" />
    <path d="M5 4h11l-3 4 3 4H5z" />
  </svg>
);

const EnvironmentalIcon = (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
    <line x1="12" y1="9" x2="12" y2="13" />
    <line x1="12" y1="17" x2="12.01" y2="17" />
  </svg>
);

// Needle points toward the low/left side of the dial, not the high side —
// this callout only ever renders when the reserve IS underfunded (see
// pickFinancialFinding below), so the icon should read as "low," like a
// fuel gauge sitting near empty, not a neutral/ambiguous meter.
const ReserveGaugeIcon = (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M3.34 19a10 10 0 1 1 17.32 0" />
    <path d="M12 14 8 10" />
    <circle cx="12" cy="14" r="1" fill="currentColor" stroke="none" />
  </svg>
);

const AssessmentIcon = (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <path d="M6 2h9l3 3v15a1 1 0 0 1-1 1H6a1 1 0 0 1-1-1V3a1 1 0 0 1 1-1z" />
    <path d="M15 2v3h3" />
    <line x1="8" y1="10" x2="16" y2="10" />
    <line x1="8" y1="14" x2="16" y2="14" />
    <line x1="8" y1="18" x2="13" y2="18" />
  </svg>
);

const GovernanceCadenceIcon = (
  <svg width="100%" height="100%" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden>
    <rect x="3" y="4" width="18" height="17" rx="2" />
    <line x1="3" y1="9" x2="21" y2="9" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="7" y1="14" x2="9" y2="14" />
    <line x1="11" y1="14" x2="13" y2="14" />
    <line x1="15" y1="14" x2="17" y2="14" />
  </svg>
);

export const HERO_ICON_MAP: Record<HOAHeroVariant, React.ReactElement> = {
  'environmental-covenant': EnvironmentalIcon,
  litigation: LitigationIcon,
  delinquency: DelinquencyIcon,
  violation: ViolationIcon,
  assessment: AssessmentIcon,
  'reserve-underfunded': ReserveGaugeIcon,
  'governance-cadence': GovernanceCadenceIcon,
};

// Same 70%/30% reserve-health thresholds HOADetailView's reserveBarTone()
// uses for the trend chart bars — defined here (and imported there) so the
// hero card's "underfunded" flag and the trend chart's color-coding can
// never silently disagree about what counts as healthy.
export const RESERVE_HEALTHY_TARGET_PCT = 70;
export const RESERVE_CRITICAL_PCT = 30;

// Minimum consecutive "No meeting held" months (most recent, unbroken) before
// it's worth flagging — one skipped month is normal HOA life; a real
// governance gap (the Wild Rose Way incident: Nov+Dec 2024 back to back)
// is two or more. Exported so BoardActivityModule flags the same gap inline
// on the timeline that the hero card surfaces at the top.
export const GOVERNANCE_CADENCE_GAP_THRESHOLD = 2;

/**
 * Counts the most recent unbroken run of "No meeting held" entries, walking
 * backward from the latest meeting record until a held meeting breaks the
 * streak. Shared between the hero-finding pick below and BoardActivityModule
 * so the summary card and detail page never disagree on the number.
 */
export function computeConsecutiveMonthsWithoutMeeting(
  meetings: HOABoardMeetingEntry[] | null | undefined,
): number {
  if (!meetings || meetings.length === 0) return 0;
  const sorted = [...meetings].sort((a, b) => a.date.localeCompare(b.date));
  let count = 0;
  for (let i = sorted.length - 1; i >= 0; i--) {
    if (sorted[i].heldMeeting) break;
    count++;
  }
  return count;
}

/**
 * Picks up to one hero finding per domain (legal/environmental, financial,
 * governance) — at most 3 total, fewer when a domain is clean. Each domain
 * checks its own findings in priority order and stops at the first match;
 * domains are independent of each other so, e.g., a delinquency doesn't
 * suppress an environmental covenant or vice versa.
 */
export function pickHOAHeroFindings(
  findings: HOAFindings | null | undefined,
  boardMinutes?: HOABoardMinutesFindings | null,
  environmentalFindings?: HOAEnvironmentalFindings | null,
  annualBudget?: HOAAnnualBudgetFindings | null,
): HOAHeroFinding[] {
  const results: HOAHeroFinding[] = [];

  const legalFinding = pickLegalFinding(findings, environmentalFindings);
  if (legalFinding) results.push(legalFinding);

  const financialFinding = pickFinancialFinding(findings, annualBudget);
  if (financialFinding) results.push(financialFinding);

  const governanceFinding = pickGovernanceCadenceFinding(boardMinutes);
  if (governanceFinding) results.push(governanceFinding);

  return results;
}

// Domain: legal/environmental — covenant > pending litigation > open violation.
function pickLegalFinding(
  findings: HOAFindings | null | undefined,
  environmentalFindings: HOAEnvironmentalFindings | null | undefined,
): HOAHeroFinding | null {
  const environmentalRisk = environmentalFindings?.risks.find(
    (risk) =>
      risk.category === 'environmental-covenant' || risk.category === 'environmental-monitoring',
  );
  // Headlines are the bare subject only (what this concerns) — never the
  // verdict (detected/pending/open/delinquent/active/underfunded/gap).
  // Status lives entirely in the detail line, consistently across every
  // variant, so all three triptych panels read the same way at a glance.
  if (environmentalRisk) {
    const environmentalSubject = /groundwater/i.test(environmentalRisk.summary)
      ? 'groundwater contamination'
      : 'environmental contamination';
    return {
      variant: 'environmental-covenant',
      tone: 'critical',
      // Consumer-facing risk domain; the expanded detail retains the precise
      // recorded-covenant terminology and source evidence.
      headline: 'Environmental',
      detail: `Recorded covenant flags ${environmentalSubject} for buyer review.`,
    };
  }

  const pendingLitigation = findings?.litigation.find((l) => l.status === 'pending');
  if (pendingLitigation) {
    return {
      variant: 'litigation',
      tone: 'critical',
      headline: 'Litigation',
      detail: 'Pending litigation may affect the property; counsel review is needed.',
    };
  }

  const openViolation = findings?.violations.find((v) => v.status === 'open');
  if (openViolation) {
    return {
      variant: 'violation',
      tone: 'urgent',
      headline: 'Violation',
      detail: 'Open HOA violation may affect closing and requires resolution.',
    };
  }

  return null;
}

// Domain: financial — delinquency > active special assessment > underfunded reserve.
function pickFinancialFinding(
  findings: HOAFindings | null | undefined,
  annualBudget: HOAAnnualBudgetFindings | null | undefined,
): HOAHeroFinding | null {
  if (findings?.delinquencyStatus === 'delinquent') {
    const delinquencyDetail =
      findings.delinquentAmount != null
        ? `Delinquent — ${fmtMoney(findings.delinquentAmount)} owed, can become a lien at transfer.`
        : 'Delinquent — unresolved dues can become a lien against the unit at transfer.';
    return {
      variant: 'delinquency',
      tone: 'critical',
      headline: 'HOA Dues',
      detail: delinquencyDetail,
    };
  }

  const activeAssessment = findings?.specialAssessments.find(
    (s) => s.status === 'active' || s.status === 'approved',
  );
  if (activeAssessment) {
    return {
      variant: 'assessment',
      tone: 'urgent',
      headline: 'Special Assessment',
      detail:
        activeAssessment.amount != null
          ? `Active — ${fmtMoney(activeAssessment.amount)}; review scope and payment terms.`
          : 'Active assessment may add buyer cost; review payment terms.',
    };
  }

  const reservePct = annualBudget?.reservePercentFunded;
  if (reservePct != null && reservePct < RESERVE_HEALTHY_TARGET_PCT) {
    return {
      variant: 'reserve-underfunded',
      tone: reservePct < RESERVE_CRITICAL_PCT ? 'critical' : 'urgent',
      headline: 'Reserve Fund',
      detail: `Underfunded — ${reservePct.toFixed(0)}% versus ${RESERVE_HEALTHY_TARGET_PCT}% target; review reserve exposure.`,
    };
  }

  return null;
}

function pickGovernanceCadenceFinding(
  boardMinutes: HOABoardMinutesFindings | null | undefined,
): HOAHeroFinding | null {
  const gapMonths = computeConsecutiveMonthsWithoutMeeting(boardMinutes?.meetings);
  if (gapMonths < GOVERNANCE_CADENCE_GAP_THRESHOLD) return null;

  return {
    variant: 'governance-cadence',
    tone: 'urgent',
    headline: 'Board Meeting',
    detail: `No meetings held for ${gapMonths} consecutive months, indicating a governance gap.`,
  };
}

// Triptych — up to one panel per domain (legal, financial, governance), so
// the three questions a buyer actually weighs separately read as separate
// panels, not a merged list. Icons are neutral (COLORS.textDim), not
// tone-colored — with three panels side by side, three different alarm
// colors competing for attention was busier than the information warranted.
export function HOAHeroContent({
  findings,
  stacked = false,
  showDetailTooltips = false,
}: {
  findings: HOAHeroFinding[];
  /** Stacks panels as rows instead of side-by-side columns. Off by default
   *  so existing callers (e.g. HOAChecklistCard on ConditionScoreView) keep
   *  the triptych; HOADetailView's SnapshotModule passes this on mobile
   *  only, where three columns don't fit. */
  stacked?: boolean;
  /** Show the same compact phrase on hover in summary-card contexts. */
  showDetailTooltips?: boolean;
}) {
  if (findings.length === 0) return null;

  return (
    <div style={{ display: 'flex', flexDirection: stacked ? 'column' : 'row', gap: 20 }}>
      {findings.map((finding, i) => (
        <Tooltip
          key={finding.variant}
          title={finding.detail}
          disableHoverListener={!showDetailTooltips}
          enterDelay={150}
          placement="bottom"
          arrow>
          <div
            style={{
              flex: stacked ? undefined : 1,
              minWidth: 0,
              display: 'flex',
              flexDirection: stacked ? 'row' : 'column',
              alignItems: stacked ? 'center' : undefined,
              gap: stacked ? 16 : 8,
              paddingLeft: !stacked && i > 0 ? 24 : 0,
              paddingTop: stacked && i > 0 ? 16 : 0,
            }}>
            <div
              style={{
                color: COLORS.textDim,
                width: stacked ? 24 : 22,
                height: stacked ? 24 : 22,
                flexShrink: 0,
              }}>
              {HERO_ICON_MAP[finding.variant]}
            </div>
            <div style={stacked ? { flex: 1, minWidth: 0 } : undefined}>
              <div style={{ fontSize: 13, fontWeight: 600, color: COLORS.text, lineHeight: 1.25, marginBottom: 3 }}>
                {finding.headline}
              </div>
              <div
                style={{
                  fontSize: 11,
                  color: COLORS.textDim,
                  lineHeight: 1.4,
                  display: '-webkit-box',
                  WebkitLineClamp: 2,
                  WebkitBoxOrient: 'vertical',
                  overflow: 'hidden',
                  textWrap: 'balance',
                }}>
                {finding.detail}
              </div>
            </div>
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

export interface HOAChecklistCardProps {
  checklist: ChecklistItem[];
  findings?: HOAFindings;
  boardMinutesFindings?: HOABoardMinutesFindings;
  environmentalFindings?: HOAEnvironmentalFindings;
  annualBudget?: HOAAnnualBudgetFindings;
  /** Whether the accordion starts open. Defaults false (matches the
   *  pillar accordion behavior on ContextScoreView). */
  defaultOpen?: boolean;
}

const DISCLOSURE_READINESS_GROUPS: Array<{
  tier: HOADisclosureTier;
  label: string;
  detail: string;
}> = [
  {
    tier: 'STATUTORY_REQUIRED',
    label: 'Required',
    detail: 'Core California transfer disclosures',
  },
  {
    tier: 'STATUTORY_CONDITIONAL',
    label: 'Conditional',
    detail: 'Provided when applicable to this sale',
  },
  {
    tier: 'BUYER_REQUESTED',
    label: 'On request',
    detail: 'Provided when requested by the buyer',
  },
  {
    tier: 'SUPPLEMENTAL',
    label: 'Supplemental',
    detail: 'Supporting diligence beyond the statutory package',
  },
];

const fmtMoney = (n: number | null | undefined): string => {
  if (n == null) return '—';
  return `$${n.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;
};

/**
 * Due-diligence checklist tracking which HOA disclosure documents should
 * exist vs. what's actually on file — shared between advisor and client so
 * a gap in the packet gets caught before it becomes a Wild Rose Way-style
 * surprise late in the transaction. Mirrors the NHD accordion visual
 * language so this reads as "another category," not a foreign block.
 */
export const HOAChecklistCard = ({
  checklist,
  findings,
  boardMinutesFindings,
  environmentalFindings,
  annualBudget,
  defaultOpen = false,
}: HOAChecklistCardProps) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [isOpen, setIsOpen] = useState(defaultOpen);

  if (!checklist.length) return null;

  const disclosureGroups = DISCLOSURE_READINESS_GROUPS.map((group) => {
    const items = checklist.filter((item) => getHOADisclosureTier(item) === group.tier);
    return {
      ...group,
      items,
      onFileCount: items.filter((item) => item.status === 'RECEIVED').length,
    };
  }).filter((group) => group.items.length > 0);
  const requiredGroup = disclosureGroups.find((group) => group.tier === 'STATUTORY_REQUIRED');
  const requiredOnFile = requiredGroup?.onFileCount ?? 0;
  const requiredTotal = requiredGroup?.items.length ?? 0;
  const heroFindings = pickHOAHeroFindings(
    findings,
    boardMinutesFindings,
    environmentalFindings,
    annualBudget,
  );

  return (
    <div
      style={{
        background: COLORS.card,
        backgroundImage: 'var(--tx-surface-light)',
        boxShadow: 'var(--tx-shadow)',
        borderRadius: '24px',
        overflow: 'hidden',
      }}>
      {/* the card's title, as its neighbours (Permit history) carry one, with the way into the full report */}
      <div style={{ display: 'flex', alignItems: 'center', padding: '0 20px', height: 60, gap: 10 }}>
        <span style={{ fontSize: 17, fontWeight: 400, letterSpacing: '-0.015em', color: 'var(--tx-text)', whiteSpace: 'nowrap' }}>
          HOA
        </span>
        <div style={{ flex: 1 }} />
        <Link
          to="/dashboard/hoa"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 4,
            color: COLORS.textDim,
            fontSize: 12,
            fontWeight: 500,
            textDecoration: 'none',
          }}>
          Full report <ChevronRight size={14} />
        </Link>
      </div>

      {/* sections part by space, not rules: the flags, the fees, the readiness */}
      {heroFindings.length > 0 && (
        <div style={{ padding: '0 20px 18px' }}>
          <HOAHeroContent findings={heroFindings} stacked={isMobile} showDetailTooltips />
        </div>
      )}
      {findings && (
        <div style={{ padding: '20px 20px 18px' }}>
          <DemandStatementSummary findings={findings} annualBudget={annualBudget} />
        </div>
      )}

      <button
        type="button"
        aria-expanded={isOpen}
        onClick={() => setIsOpen((v) => !v)}
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          padding: '0 20px',
          height: 50,
          border: 0,
          background: 'transparent',
          cursor: 'pointer',
          gap: 10,
          font: 'inherit',
          textAlign: 'left',
        }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>Disclosure readiness</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)', whiteSpace: 'nowrap' }}>
          {requiredOnFile} of {requiredTotal} required on file
        </span>
        <div style={{ flex: 1 }} />
        <span
          style={{
            color: COLORS.textDim,
            display: 'inline-flex',
            alignItems: 'center',
            transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.18s ease',
          }}>
          <ChevronDown size={14} />
        </span>
      </button>

      <div style={{ padding: '0 20px 16px', display: 'flex', flexDirection: 'column', gap: 0 }}>
        {disclosureGroups.map((group) => (
          <DisclosureReadinessSummary key={group.tier} {...group} />
        ))}
      </div>

      {isOpen && (
        <div
          style={{
            padding: '0 20px 20px',
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}>
          <div style={{ fontSize: 11, color: COLORS.textDim, lineHeight: 1.55 }}>
            Required documents are measured against California&apos;s core transfer-disclosure
            package. Conditional items are not treated as missing unless they apply; board minutes
            are supplied on request.
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
            {disclosureGroups.map((group) => (
              <DisclosureDocumentGroup key={group.tier} {...group} />
            ))}
          </div>

          <Link
            to="/dashboard/hoa"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              alignSelf: 'flex-end',
              gap: 4,
              color: COLORS.textDim,
              fontSize: 11,
              textDecoration: 'none',
            }}>
            Review HOA disclosures <ChevronRight size={14} />
          </Link>
        </div>
      )}
    </div>
  );
};

function DisclosureReadinessSummary({
  label,
  items,
  onFileCount,
}: {
  label: string;
  items: ChecklistItem[];
  onFileCount: number;
}) {
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 12,
        minHeight: 28,
      }}>
      <span style={{ flex: 1, fontSize: 12, fontWeight: 500, color: 'var(--tx-text)' }}>{label}</span>
      {/* coverage at a glance: what's on file as a share of what's expected */}
      <span style={{ width: 96, height: 2, borderRadius: 1, background: 'var(--tx-fill-2)', position: 'relative', flexShrink: 0 }}>
        <span
          style={{
            position: 'absolute',
            inset: '0 auto 0 0',
            width: `${items.length ? (onFileCount / items.length) * 100 : 0}%`,
            borderRadius: 1,
            background: 'var(--tx-text)',
          }}
        />
      </span>
      <span style={{ width: 40, textAlign: 'right', fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
        {onFileCount}/{items.length}
      </span>
    </div>
  );
}

function DisclosureDocumentGroup({
  label,
  detail,
  items,
  onFileCount,
}: {
  label: string;
  detail: string;
  items: ChecklistItem[];
  onFileCount: number;
}) {
  return (
    <div style={{ padding: '10px 0' }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, padding: '0 0 4px' }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)' }}>{label}</span>
        <span style={{ flex: 1, minWidth: 0, fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)' }}>{detail}</span>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)', whiteSpace: 'nowrap' }}>
          {onFileCount}/{items.length} on file
        </span>
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        {items.map((item) => (
          <ChecklistRow key={item.id} item={item} />
        ))}
      </div>
    </div>
  );
}

// Outline, not filled — lime is reserved for a real "reviewed" concept once
// a document-detail page exists to set it. Received alone (the AI parsed
// it, nothing more) gets the softer, unfilled taupe treatment. Exported so
// HOADetailView uses the exact same icon — never drifts between the
// summary card and the detail page.
export function ReceivedIcon() {
  return (
    <svg width="16" height="16" viewBox="0 0 24 24" aria-label="Received">
      <circle cx="12" cy="12" r="10.5" fill="none" stroke={COLORS.accent2} strokeWidth="1.5" />
      <path
        d="M7 12.5l3 3 7-7"
        fill="none"
        stroke={COLORS.accent2}
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChecklistRow({ item }: { item: ChecklistItem }) {
  const isReceived = item.status === 'RECEIVED';
  return (
    <div
      style={{
        // on the card's 20px edge like every other row: no inset of its own
        padding: '8px 0',
        display: 'flex',
        alignItems: 'center',
        gap: 12,
      }}>
      <span
        style={{
          fontSize: 13,
          fontWeight: 500,
          color: 'var(--tx-text)',
          flex: 1,
          minWidth: 0,
        }}>
        {item.label}
      </span>
      {isReceived ? (
        <ReceivedIcon />
      ) : (
        <span
          style={{
            fontSize: 11,
            fontWeight: 500,
            color: 'var(--tx-faint)',
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
          Not on file
        </span>
      )}
    </div>
  );
}

// Only Reserve Funding's value color is meaningful (it tracks the same
// critical/urgent/healthy thresholds as the trend chart) — Assessment,
// Delinquency, and Special Assessments stay this neutral near-white
// regardless of state, so the one color that does carry meaning doesn't
// compete with three that don't.
const STAT_VALUE_COLOR = '#f3f3f3';

function DemandStatementSummary({
  findings,
  annualBudget,
}: {
  findings: HOAFindings;
  annualBudget?: HOAAnnualBudgetFindings;
}) {
  const isDelinquent = findings.delinquencyStatus === 'delinquent';
  const activeSpecialAssessments = findings.specialAssessments.filter(
    (s) => s.status === 'active' || s.status === 'approved',
  ).length;
  const reservePct = annualBudget?.reservePercentFunded;
  const reserveTone =
    reservePct == null
      ? undefined
      : reservePct < RESERVE_CRITICAL_PCT
        ? COLORS.critical
        : reservePct < RESERVE_HEALTHY_TARGET_PCT
          ? COLORS.urgent
          : COLORS.lime;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 8 }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: 'var(--tx-muted)', flex: 1, minWidth: 0 }}>
          Fees &amp; assessments
        </span>
        <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-faint)', whiteSpace: 'nowrap', flexShrink: 0 }}>
          From the demand statement
        </span>
      </div>
      {/* a hairline stat row (the HomeDNA stats): value over label, parted by hairlines, no boxes */}
      {/* a phone takes the four as two rows of two, each row starting flush left */}
      <style>
        {'.hx-stat-row { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); } ' +
          '.hx-stat-row > * + * { border-left: 1px solid var(--tx-line); padding-left: 16px; } ' +
          '.hx-stat-row > * { padding-right: 16px; } ' +
          '@media (max-width: 599px) { .hx-stat-row { grid-template-columns: repeat(2, minmax(0, 1fr)); row-gap: 16px; } ' +
          '.hx-stat-row > :nth-child(odd) { border-left: 0; padding-left: 0; } }'}
      </style>
      <div className="hx-stat-row">
        <SummaryStat
          label="Assessment"
          value={fmtMoney(findings.assessmentAmount)}
          detail={findings.assessmentFrequency ?? undefined}
          tone={STAT_VALUE_COLOR}
        />
        <SummaryStat
          label="Delinquency"
          value={isDelinquent ? fmtMoney(findings.delinquentAmount) : 'Current'}
          tone={STAT_VALUE_COLOR}
        />
        <SummaryStat
          label="Special assessments"
          value={String(activeSpecialAssessments)}
          tone={STAT_VALUE_COLOR}
        />
        <SummaryStat
          label="Reserve funding"
          value={reservePct == null ? '—' : `${reservePct.toFixed(0)}%`}
          tone={reserveTone}
        />
      </div>
    </div>
  );
}

function SummaryStat({
  label,
  value,
  detail,
  tone,
}: {
  label: string;
  value: string;
  detail?: string;
  tone?: string;
}) {
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 0 }}>
      <div
        style={{
          fontSize: 22,
          fontWeight: 200,
          letterSpacing: '-0.04em',
          lineHeight: 1,
          color: tone ?? 'var(--tx-text)',
          whiteSpace: 'nowrap',
        }}>
        {value}
        {detail && (
          <span style={{ fontSize: 11, fontWeight: 500, letterSpacing: 0, color: 'var(--tx-muted)', marginLeft: 4 }}>
            /{detail}
          </span>
        )}
      </div>
      <div style={{ fontSize: 11, fontWeight: 500, color: 'var(--tx-muted)' }}>{label}</div>
    </div>
  );
}

export default HOAChecklistCard;
